// leaseScenarios.js — v1.0.0 Phase 3 (Lease-Leverage Module)
//
// Thin data-access layer for the 2 lease_scenarios endpoints. Server
// owns the NOI math: upsertLeaseScenarios sends lot_revenues + scenario_type
// and the function reads the expense stack from lease_terms, sums revenue
// server-side, and writes total_monthly_revenue / monthly_noi / annual_noi
// back. Clients should ignore any locally-computed NOI on the wire.
import { supabase } from './supabaseClient';
import { API_BASE_URL } from '../services/api';

const NOT_SIGNED_IN = {
  data: null,
  error: { message: 'Not signed in. Please sign in and try again.' },
};
const NOT_CONFIGURED = {
  data: null,
  error: {
    message:
      'Supabase is not configured. Fill in frontend/.env (REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY) and restart the dev server.',
  },
};

async function callApi(path, init = {}) {
  if (!supabase) return NOT_CONFIGURED;
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) return NOT_SIGNED_IN;
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        ...(init.headers || {}),
      },
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false) {
      return {
        data: null,
        error: {
          message: payload?.error || `Request failed (HTTP ${response.status}).`,
          status: response.status,
          payload,
        },
      };
    }
    return { data: payload, error: null };
  } catch (err) {
    return { data: null, error: { message: err?.message || 'Network error.' } };
  }
}

/**
 * Upsert one or more scenarios on a lease_terms row. Pass any subset of
 * {conservative, hybrid, str} — the function leaves unmentioned scenarios
 * untouched. Server computes NOI; any client-supplied totals are ignored.
 *
 * @param {string} leaseTermsId
 * @param {Array<{ scenario_type, lot_revenues: Array<{name, monthly_revenue}>, notes? }>} scenarios
 */
export async function upsertLeaseScenarios(leaseTermsId, scenarios) {
  return callApi('/upsert-lease-scenarios', {
    method: 'POST',
    body: JSON.stringify({ lease_terms_id: leaseTermsId, scenarios: scenarios || [] }),
  });
}

/**
 * Fetch all scenarios for a lease_terms, sorted conservative → hybrid → str.
 * Returns { data: { scenarios: [...] }, error }. Empty array if nothing saved yet.
 */
export async function getLeaseScenarios(leaseTermsId) {
  return callApi(`/get-lease-scenarios?lease_terms_id=${encodeURIComponent(leaseTermsId)}`, {
    method: 'GET',
  });
}
